import React, { useEffect, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Highlight } from '@tiptap/extension-highlight';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extension-placeholder';
import {
  Bold, Italic, List, ListOrdered, Highlighter as HighlighterIcon, Minus,
  Table as TableIcon, Image as ImageIcon, Youtube, Link2, Link2Off,
  Heading2, Heading3, Pilcrow,
} from 'lucide-react';
import { marked } from 'marked';

// Heuristic — older notes were stored as plain markdown ("# Title\n- item").
// New notes are stored as HTML. If the content has no HTML angle-brackets, we
// transparently upgrade it to HTML on first edit so the editor renders correctly.
const looksLikeHtml = (s) => typeof s === 'string' && /<\w+[\s>]/.test(s);

export const normalizeNoteContent = (value) => {
  if (!value) return '';
  if (looksLikeHtml(value)) return value;
  // Treat as markdown and convert. marked is sync by default.
  try {
    return marked.parse(value, { breaks: true, gfm: true });
  } catch (_) {
    return value;
  }
};

// Convert a plain URL (YouTube, Vimeo, Loom, Google Docs/Slides, Figma, etc.)
// into an embeddable iframe. Falls back to a clickable anchor if we don't
// recognise the provider.
const buildEmbedHtml = (rawUrl) => {
  if (!rawUrl) return '';
  let url = rawUrl.trim();
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    let src = null;
    // YouTube
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = u.searchParams.get('v');
      if (id) src = `https://www.youtube.com/embed/${id}`;
    } else if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      if (id) src = `https://www.youtube.com/embed/${id}`;
    } else if (host === 'loom.com') {
      src = url.replace('/share/', '/embed/');
    } else if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id) src = `https://player.vimeo.com/video/${id}`;
    } else if (host.endsWith('docs.google.com') || host.endsWith('drive.google.com') || host.endsWith('figma.com')) {
      src = url; // these support direct iframe embedding
    }
    if (src) {
      return `<div class="note-embed" data-embed-src="${src}"><iframe src="${src}" frameborder="0" allowfullscreen loading="lazy" referrerpolicy="no-referrer"></iframe></div><p></p>`;
    }
  } catch (_) { /* fall through */ }
  // Fallback — render as a link.
  return `<p><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></p>`;
};

const ToolbarButton = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    aria-label={title}
    data-testid={testid}
    className={`h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-200/70 transition-colors ${
      active ? 'bg-slate-200 text-slate-900' : ''
    }`}
  >
    {children}
  </button>
);

const Sep = () => <span className="mx-0.5 h-5 w-px bg-slate-300/70" aria-hidden="true" />;

const RichNoteEditor = ({ value, onChange, placeholder = 'Write your thoughts…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        horizontalRule: { HTMLAttributes: { class: 'note-divider' } },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: false, HTMLAttributes: { class: 'note-image' } }),
      Highlight.configure({ multicolor: false, HTMLAttributes: { class: 'note-highlight' } }),
      Table.configure({ resizable: false, HTMLAttributes: { class: 'note-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    content: normalizeNoteContent(value),
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Empty editor in tiptap is "<p></p>" — normalise to '' so save buttons
      // can rely on falsy checks.
      onChange(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class:
          'rich-note-editor prose prose-sm max-w-none focus:outline-none min-h-[180px] px-3 py-2 bg-white/60 rounded-b-lg',
      },
    },
  });

  // Sync external value changes (e.g. AI-generated insert). Skip when the
  // editor already has matching content to avoid cursor jumps while typing.
  useEffect(() => {
    if (!editor) return;
    const incoming = normalizeNoteContent(value);
    if (editor.getHTML() !== incoming && incoming !== '<p></p>') {
      editor.commands.setContent(incoming || '', false);
    }
    // eslint-disable-next-line
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const insertImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL');
    if (!url) return;
    editor.chain().focus().setImage({ src: url, alt: '' }).run();
  }, [editor]);

  const insertEmbed = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Embed URL (YouTube, Loom, Vimeo, Google Docs, Figma…)');
    if (!url) return;
    editor.chain().focus().insertContent(buildEmbedHtml(url)).run();
  }, [editor]);

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className="rounded-lg border border-amber-300/60 bg-white/40 overflow-hidden"
      data-testid="rich-note-editor"
    >
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-amber-50/70 border-b border-amber-300/60">
        <ToolbarButton title="Paragraph" testid="rt-paragraph"
          active={editor.isActive('paragraph') && !editor.isActive('heading')}
          onClick={() => editor.chain().focus().setParagraph().run()}>
          <Pilcrow size={15} />
        </ToolbarButton>
        <ToolbarButton title="Heading 2" testid="rt-h2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 size={15} />
        </ToolbarButton>
        <ToolbarButton title="Heading 3" testid="rt-h3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 size={15} />
        </ToolbarButton>
        <Sep />
        <ToolbarButton title="Bold" testid="rt-bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={15} />
        </ToolbarButton>
        <ToolbarButton title="Italic" testid="rt-italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={15} />
        </ToolbarButton>
        <Sep />
        <ToolbarButton title="Bullet list" testid="rt-bullet"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={15} />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" testid="rt-ordered"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={15} />
        </ToolbarButton>
        <Sep />
        <ToolbarButton title="Highlight block" testid="rt-highlight"
          active={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}>
          <HighlighterIcon size={15} />
        </ToolbarButton>
        <ToolbarButton title="Divider" testid="rt-divider"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus size={15} />
        </ToolbarButton>
        <Sep />
        <ToolbarButton title="Table" testid="rt-table" onClick={insertTable}>
          <TableIcon size={15} />
        </ToolbarButton>
        <ToolbarButton title="Image" testid="rt-image" onClick={insertImage}>
          <ImageIcon size={15} />
        </ToolbarButton>
        <ToolbarButton title="Embed (YouTube / Loom / Figma…)" testid="rt-embed" onClick={insertEmbed}>
          <Youtube size={15} />
        </ToolbarButton>
        <Sep />
        <ToolbarButton title="Insert link" testid="rt-link"
          active={editor.isActive('link')}
          onClick={setLink}>
          <Link2 size={15} />
        </ToolbarButton>
        <ToolbarButton title="Remove link" testid="rt-unlink"
          onClick={() => editor.chain().focus().unsetLink().run()}>
          <Link2Off size={15} />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} data-testid="note-content-editor" />
    </div>
  );
};

export default RichNoteEditor;
